import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { WP_FORM_ERROR_MESSAGES } from '../../../dashboard/dialogs/add-website-dialog/add-website-dialog.const';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { DatabaseService } from '../../../../core/services/database.service';
import { DatabaseStatus } from '../../../../shared/models/database.models';
import { InstallWP } from '../../../../shared/models/wordpress.models';

@Component({
  selector: 'app-install-wordpress-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    MatCheckboxModule,
  ],
  templateUrl: './install-wordpress-dialog.component.html',
  styleUrl: './install-wordpress-dialog.component.scss',
})
export class InstallWordpressDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<InstallWordpressDialogComponent>);

  readonly data = inject<DialogData & { websiteId: number; domain: string; forceInstall: boolean }>(
    MAT_DIALOG_DATA,
  );

  private databaseService = inject(DatabaseService);

  private fb = inject(FormBuilder);

  readonly wpErrors = WP_FORM_ERROR_MESSAGES;

  databases = signal<{ label: string; value: string }[]>([]);

  wordpressForm = this.fb.nonNullable.group({
    websiteId: [0, [Validators.required]],
    title: ['', [Validators.required]], // To be removed
    user: ['', Validators.required],
    password: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    directory: [''],
    enableRedisCache: [false],
    enableW3TotalCache: [false], // Utility control
    enableWooCommerce: [false], // Utility control
    plugins: [[] as string[]],
    forceInstall: [false],
    databaseId: [0],
  });

  ngOnInit(): void {
    this.wordpressForm.controls.title.setValue(this.domainToTitle(this.data.domain));
    this.wordpressForm.controls.websiteId.setValue(this.data.websiteId);
    this.wordpressForm.controls.forceInstall.setValue(this.data.forceInstall);

    this.wordpressForm.controls.directory.valueChanges.subscribe((value) => {
      if (value.length) {
        this.wordpressForm.controls.databaseId.reset();
        this.wordpressForm.controls.databaseId.disable();
      } else this.wordpressForm.controls.databaseId.enable();
      this.wordpressForm.controls.databaseId.updateValueAndValidity();
    });

    this.databaseService.getByWebsiteId(this.data.websiteId).subscribe((databases) => {
      const items = databases
        .filter(
          (database) =>
            database.status === DatabaseStatus.ACTIVE || database.status === DatabaseStatus.DONE,
        )
        .map((d) => ({ label: d.name, value: d.id.toString() }));
      this.databases.set(items);
    });
  }

  submit(): void {
    if (this.wordpressForm.invalid) return;

    const { databaseId, enableW3TotalCache, enableWooCommerce, ...rest } =
      this.wordpressForm.getRawValue();

    rest.plugins = [enableW3TotalCache ? 'w3tc' : '', enableWooCommerce ? 'wc' : ''].filter(
      (action) => action,
    );

    if (databaseId) (rest as InstallWP).databaseId = +databaseId;

    this.dialogRef.close(rest);
  }

  close(): void {
    this.dialogRef.close();
  }

  private domainToTitle(domain: string): string {
    return domain
      .replace(/^www\./i, '') // remove www.
      .replace(/\.[^.]+(?:\.[^.]+)?$/, '') // remove TLD(s)
      .replace(/[-_]+/g, ' ') // hyphens/underscores -> spaces
      .replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case
  }
}
